import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Drawer } from 'antd';
import { authAPI } from '../../services/authAPI';
import { restartOnboarding } from '../onboarding/OnboardingTour';
import { useAlerts } from '../../hooks/useAlerts';
import NotificationList from '../common/NotificationList';

interface MenuItem { icon: string; label: string; path: string; }
interface MenuGroup { title: string; items: MenuItem[]; }

// 与桌面 Sidebar 一致的可见菜单(研究/策略/系统),移动端收进「更多」抽屉
const MENU_GROUPS: MenuGroup[] = [
  {
    title: '研究',
    items: [
      { icon: 'ri-database-2-line', label: '因子工作台', path: '/app/factor-workbench' },
      { icon: 'ri-flask-line', label: '批量实验', path: '/app/batch-experiments' },
    ],
  },
  {
    title: '策略',
    items: [
      { icon: 'ri-dashboard-3-line', label: '策略工作台', path: '/app/strategy-workbench' },
      { icon: 'ri-robot-line', label: 'AI推荐', path: '/app/recommendations' },
      { icon: 'ri-wallet-3-line', label: '我的持仓', path: '/app/my-portfolio' },
      { icon: 'ri-article-line', label: '研报研究', path: '/app/analyst' },
    ],
  },
  {
    title: '系统',
    items: [
      { icon: 'ri-dashboard-line', label: '监控面板', path: '/app/monitoring' },
      { icon: 'ri-question-line', label: '帮助中心', path: '/app/help' },
    ],
  },
];

/**
 * 移动端底部标签栏:推荐 / 持仓 / 通知 / 更多。
 * 桌面端不渲染(见 Layout)。通知走 useAlerts(与桌面铃铛共用),红点角标为未读数。
 */
export default function MobileTabBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const { notifications, unreadCount, markRead, markAllRead } = useAlerts();

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    try { await authAPI.logout(); } catch { /* ignore */ }
    finally {
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      navigate('/auth/login');
    }
  };

  const go = (path: string) => { setMoreOpen(false); navigate(path); };

  const tabBase = 'flex-1 flex flex-col items-center justify-center gap-0.5 py-2 relative';
  const iconCls = (on: boolean) => `text-xl ${on ? 'text-teal-600' : 'text-gray-400'}`;
  const lblCls = (on: boolean) => `text-[11px] ${on ? 'text-teal-600 font-medium' : 'text-gray-500'}`;

  return (
    <>
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 shadow-[0_-1px_8px_rgba(0,0,0,0.05)] pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-stretch">
          <button className={tabBase} onClick={() => navigate('/app/recommendations')}>
            <i className={`ri-robot-line ${iconCls(isActive('/app/recommendations'))}`}></i>
            <span className={lblCls(isActive('/app/recommendations'))}>推荐</span>
          </button>
          <button className={tabBase} onClick={() => navigate('/app/my-portfolio')}>
            <i className={`ri-wallet-3-line ${iconCls(isActive('/app/my-portfolio'))}`}></i>
            <span className={lblCls(isActive('/app/my-portfolio'))}>持仓</span>
          </button>
          <button className={tabBase} onClick={() => setNotifOpen(true)}>
            <i className={`ri-notification-3-line ${iconCls(notifOpen)}`}></i>
            {unreadCount > 0 && (
              <span className="absolute top-1 right-[22%] min-w-[16px] h-4 px-1 bg-gradient-to-br from-red-500 to-pink-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
            <span className={lblCls(notifOpen)}>通知</span>
          </button>
          <button className={tabBase} onClick={() => setMoreOpen(true)}>
            <i className={`ri-menu-line ${iconCls(moreOpen)}`}></i>
            <span className={lblCls(moreOpen)}>更多</span>
          </button>
        </div>
      </nav>

      {/* 通知 sheet */}
      <Drawer
        title="通知中心"
        placement="right"
        width="86%"
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
        styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' } }}
      >
        <NotificationList
          notifications={notifications}
          unreadCount={unreadCount}
          markRead={markRead}
          markAllRead={markAllRead}
          listClassName="flex-1"
        />
      </Drawer>

      {/* 更多 抽屉:完整菜单 + 引导 + 退出 */}
      <Drawer
        title="更多"
        placement="right"
        width="80%"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        styles={{ body: { padding: '12px 0' } }}
      >
        {MENU_GROUPS.map((g) => (
          <div key={g.title} className="mb-3">
            <div className="px-5 mb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">{g.title}</div>
            {g.items.map((it) => (
              <button
                key={it.path}
                onClick={() => go(it.path)}
                className={`w-full flex items-center space-x-3 px-5 py-3 text-left transition-colors ${
                  isActive(it.path) ? 'bg-teal-50 text-teal-700' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className={`${it.icon} text-lg`}></i>
                <span className="text-sm font-medium">{it.label}</span>
              </button>
            ))}
          </div>
        ))}
        <div className="border-t border-gray-100 pt-2">
          <button
            onClick={() => { setMoreOpen(false); restartOnboarding(); }}
            className="w-full flex items-center space-x-3 px-5 py-3 text-left text-gray-700 hover:bg-gray-50"
          >
            <i className="ri-guide-line text-lg"></i>
            <span className="text-sm font-medium">重看引导</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-5 py-3 text-left text-red-600 hover:bg-red-50"
          >
            <i className="ri-logout-box-line text-lg"></i>
            <span className="text-sm font-medium">退出登录</span>
          </button>
        </div>
      </Drawer>
    </>
  );
}
